import { useEffect } from 'react'
import { create } from 'zustand'
import { useUi } from '../store/ui'

type SharedDraft = { target: string; text: string; revision: number; updated_at: number }
type PendingDraft = { text: string; base_revision: number }
type SyncCache = { revisions: Record<string, number>; pending: Record<string, PendingDraft> }
const ENDPOINT = '/api/ui_prefs/composer_drafts'
const CACHE_KEY = 'lane-control-ui:shared-drafts'

function loadCache(): SyncCache {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null')
    if (value && typeof value === 'object' && 'revisions' in value && 'pending' in value) {
      const revisions: Record<string, number> = {}
      const pending: Record<string, PendingDraft> = {}
      if (value.revisions && typeof value.revisions === 'object') {
        for (const [key, revision] of Object.entries(value.revisions)) {
          if (Number.isSafeInteger(revision) && revision >= 0) revisions[key] = revision
        }
      }
      if (value.pending && typeof value.pending === 'object') {
        for (const [key, draft] of Object.entries(value.pending)) {
          if (draft && typeof draft === 'object' && 'text' in draft && typeof draft.text === 'string'
            && 'base_revision' in draft && typeof draft.base_revision === 'number'
            && Number.isSafeInteger(draft.base_revision) && draft.base_revision >= 0) {
            pending[key] = { text: draft.text, base_revision: draft.base_revision }
          }
        }
      }
      return { revisions, pending }
    }
  } catch { /* Old drafts remain in the UI store. */ }
  // An old device may import its drafts once, but cannot overwrite a shared row.
  return { revisions: {}, pending: Object.fromEntries(
    Object.entries(useUi.getState().draftsByChat).map(([key, text]) => [key, { text, base_revision: 0 }]),
  ) }
}

const cache = loadCache()
let applyingRemote = false
let syncing: Promise<void> | null = null
let timer: ReturnType<typeof setTimeout> | null = null

export const useDraftSync = create<{
  conflicts: Record<string, SharedDraft>
  offline: boolean
}>(() => ({ conflicts: {}, offline: false }))

function saveCache() {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)) } catch { /* Keep unsaved text in memory. */ }
}

function applyDraft(draft: SharedDraft) {
  const state = useUi.getState()
  const drafts = { ...state.draftsByChat }
  const times = { ...state.draftUpdatedAt }
  if (draft.text) {
    drafts[draft.target] = draft.text
    times[draft.target] = draft.updated_at
  } else {
    delete drafts[draft.target]
    delete times[draft.target]
  }
  applyingRemote = true
  try {
    // Cancel any queued old snapshot through the store's existing immediate clear.
    state.clearDraft(draft.target)
    useUi.setState({ draftsByChat: drafts, draftUpdatedAt: times })
    localStorage.setItem('lane-control-ui:drafts', JSON.stringify(drafts))
    localStorage.setItem('lane-control-ui:draftTimes', JSON.stringify(times))
  } finally { applyingRemote = false }
}

// Recover edits whose local UI snapshot had not flushed before the app closed.
for (const [target, pending] of Object.entries(cache.pending)) {
  applyDraft({ target, text: pending.text, revision: pending.base_revision, updated_at: Date.now() })
}

function parseDraft(value: unknown): SharedDraft {
  if (!value || typeof value !== 'object'
    || !('target' in value) || typeof value.target !== 'string'
    || !('text' in value) || typeof value.text !== 'string'
    || !('revision' in value) || typeof value.revision !== 'number' || !Number.isSafeInteger(value.revision)
    || !('updated_at' in value) || typeof value.updated_at !== 'number') throw new Error('Invalid shared draft')
  return { target: value.target, text: value.text, revision: value.revision, updated_at: value.updated_at }
}

function forgetConflict(target: string) {
  const conflicts = { ...useDraftSync.getState().conflicts }
  delete conflicts[target]
  useDraftSync.setState({ conflicts })
}

export function resolveDraftConflict(target: string, choice: 'local' | 'shared') {
  const shared = useDraftSync.getState().conflicts[target]
  if (!shared) return
  cache.revisions[target] = shared.revision
  if (choice === 'local') {
    cache.pending[target] = { text: useUi.getState().draftsByChat[target] ?? '', base_revision: shared.revision }
  } else {
    delete cache.pending[target]
    applyDraft(shared)
  }
  forgetConflict(target)
  saveCache()
  void syncComposerDrafts()
}

export function syncComposerDrafts(): Promise<void> {
  if (syncing) return syncing
  syncing = (async () => {
    for (const [target, pending] of Object.entries(cache.pending)) {
      if (useDraftSync.getState().conflicts[target]) continue
      const response = await fetch(ENDPOINT, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target, ...pending }), signal: AbortSignal.timeout(10_000),
      })
      if (!response.ok && response.status !== 409) throw new Error('Draft sync unavailable')
      const result: unknown = await response.json()
      if (!result || typeof result !== 'object' || !('draft' in result)) throw new Error('Invalid draft response')
      const shared = parseDraft(result.draft)
      cache.revisions[target] = shared.revision
      if (response.status === 409) {
        useDraftSync.setState({ conflicts: { ...useDraftSync.getState().conflicts, [target]: shared } })
      } else if (cache.pending[target] === pending) {
        delete cache.pending[target]
      } else if (cache.pending[target]) {
        // Typing during a successful upload continues from that accepted revision.
        cache.pending[target].base_revision = shared.revision
      }
      saveCache()
    }
    const response = await fetch(ENDPOINT, { credentials: 'include', signal: AbortSignal.timeout(10_000) })
    if (!response.ok) throw new Error('Draft sync unavailable')
    const result: unknown = await response.json()
    if (!result || typeof result !== 'object' || !('drafts' in result) || !Array.isArray(result.drafts)) {
      throw new Error('Invalid drafts response')
    }
    for (const value of result.drafts) {
      const draft = parseDraft(value)
      if (cache.pending[draft.target]) {
        if (useDraftSync.getState().conflicts[draft.target]) {
          useDraftSync.setState({ conflicts: { ...useDraftSync.getState().conflicts, [draft.target]: draft } })
        }
        continue
      }
      if (cache.revisions[draft.target] !== draft.revision
        || (useUi.getState().draftsByChat[draft.target] ?? '') !== draft.text) applyDraft(draft)
      cache.revisions[draft.target] = draft.revision
    }
    saveCache()
    useDraftSync.setState({ offline: false })
  })().catch(() => {
    useDraftSync.setState({ offline: true })
  }).finally(() => { syncing = null })
  return syncing
}

// Observe the existing composer store, including clears after durable outbox promotion.
useUi.subscribe((state, previous) => {
  if (applyingRemote || state.draftsByChat === previous.draftsByChat) return
  for (const target of new Set([...Object.keys(state.draftsByChat), ...Object.keys(previous.draftsByChat)])) {
    const text = state.draftsByChat[target] ?? ''
    if (text === (previous.draftsByChat[target] ?? '')) continue
    cache.pending[target] = { text, base_revision: cache.pending[target]?.base_revision ?? cache.revisions[target] ?? 0 }
  }
  saveCache()
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => { void syncComposerDrafts() }, 400)
})

export function useComposerDraftSync() {
  useEffect(() => {
    const sync = () => { if (document.visibilityState !== 'hidden') void syncComposerDrafts() }
    const flush = () => { void syncComposerDrafts() }
    sync()
    const interval = window.setInterval(sync, 3_000)
    window.addEventListener('focus', sync)
    window.addEventListener('online', sync)
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', flush)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', sync)
      window.removeEventListener('online', sync)
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', flush)
    }
  }, [])
}
